import { useEffect, useRef, useState } from "react";
import { useZasob } from "../../hooks/useZasob.js";
import { api } from "../../lib/api.js";
import { MAKS_DLUGOSC_SZUKANIA_LOGOW } from "@shared/wywolaniaTypy.js";
import { formatujCzas, komunikatBleduApi, kluczeLinii, podzielLinieLogu, strefaPrzegladarki } from "../../lib/developer.js";
import { formatujLiczbe } from "../../lib/koszty.js";
import { IconKopiuj } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { Checkbox } from "../ui/Choice.js";
import { Field } from "../ui/Field.js";
import { Select } from "../ui/Select.js";
import { StanBledu, StanLadowania, StanPusty } from "../ui/stany.js";
import { BladOdswiezania } from "./BladOdswiezania.js";
import { PoleSzukania } from "./PoleSzukania.js";

/**
 * Zakladka "Logi": ogon logu aplikacji (WY-5). Obszar na --surface-sunken z wlasnym scrollem i monospace (wyjatek od TY-3
 * dla logow), poziom oznaczony kolorem I etykieta (A-7: nie sam kolor). Serwer oddaje ostatnie linie po filtrze i po
 * redakcji sekretow. Odswiezanie co 5 s mozna wylaczyc (przy czytaniu dluzszego fragmentu nie ma sensu, zeby tekst sie ruszal).
 *
 * Znaczniki czasu w pliku logu sa w UTC (pino isoTime), a reszta zakladki pokazuje czas lokalny przegladarki. Blok pokazuje
 * wiec godziny lokalne w tym samym formacie co reszta, z nazwa strefy nad blokiem i oryginalnym zapisem w dymku (title).
 *
 * Blok linii ma `aria-live="off"`: role="log" domyslnie ogłasza kazda zmiane, a przy odswiezaniu co 5 s czytnik ekranu
 * czytalby od nowa kolejne partie. Linie maja stabilne klucze (tresc plus numer wystapienia), wiec nowa linia na koncu nie
 * przemontowuje calego bloku i nie gubi polozenia przewijania.
 */

const POZIOMY = [
  { wartosc: "", etykieta: "Wszystkie poziomy" },
  { wartosc: "info", etykieta: "Info i wyżej" },
  { wartosc: "warn", etykieta: "Ostrzeżenia i wyżej" },
  { wartosc: "error", etykieta: "Tylko błędy" },
];

const LINII = 300;

function wariantPoziomu(poziom: string | null): "danger" | "warning" | "info" | "neutralny" {
  if (poziom === "error" || poziom === "fatal") return "danger";
  if (poziom === "warn") return "warning";
  if (poziom === "info") return "info";
  return "neutralny";
}

export function LogiWidok(): React.JSX.Element {
  const [poziom, setPoziom] = useState("");
  const [szukaj, setSzukaj] = useState("");
  const [automat, setAutomat] = useState(true);
  const [skopiowano, setSkopiowano] = useState(false);
  const zegarRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (zegarRef.current) clearTimeout(zegarRef.current);
    },
    [],
  );

  const klucz = `${poziom}|${szukaj}`;
  const { dane, kluczDanych, ladowanie, blad, odswiez } = useZasob(klucz, () => api.developerLogi({ linie: LINII, poziom, szukaj }), {
    co: automat ? 5_000 : undefined,
  });

  async function kopiuj(): Promise<void> {
    if (!dane) return;
    try {
      await navigator.clipboard.writeText(dane.linie.join("\n"));
      setSkopiowano(true);
      if (zegarRef.current) clearTimeout(zegarRef.current);
      zegarRef.current = setTimeout(() => setSkopiowano(false), 2000);
    } catch {
      // Schowek bywa zablokowany (HTTP, brak uprawnien): zostaje zaznaczenie tekstu recznie.
    }
  }

  // Linie z odpowiedzi na INNY filtr (poprzednie zapytanie) nie sa danymi dla tego, co wybral uzytkownik: gdy pobranie
  // nowych padnie, jest stan bledu z ponowieniem, a nie stare linie pod nowym filtrem. Przy cichym odswiezeniu tego samego
  // widoku stare linie zostaja, a nad nimi stoi komunikat.
  const bladBezZgodnychDanych = dane === null || kluczDanych !== klucz;
  const linie = dane?.linie ?? [];
  const klucze = kluczeLinii(linie);
  const strefa = strefaPrzegladarki();

  return (
    <Card
      tytul="Logi aplikacji"
      akcje={
        <>
          <Button onClick={odswiez}>Odśwież</Button>
          <Button wylaczony={!dane || dane.linie.length === 0} onClick={() => void kopiuj()}>
            <IconKopiuj size={18} />
            {skopiowano ? "Skopiowano" : "Kopiuj"}
          </Button>
        </>
      }
    >
      <div className="developer-filtry">
        <Field etykieta="Poziom" id="dev-logi-poziom">
          {(a) => <Select {...a} wartosc={poziom} opcje={POZIOMY} onZmiana={setPoziom} />}
        </Field>
        <PoleSzukania
          id="dev-logi-szukaj"
          etykieta="Szukaj w logach"
          wartosc={szukaj}
          onZmiana={setSzukaj}
          maksDlugosc={MAKS_DLUGOSC_SZUKANIA_LOGOW}
          podpowiedz="Szuka w oryginalnym zapisie linii, czyli także w czasie UTC (nie w godzinach widocznych tutaj)."
        />
        <div className="developer-filtry__checkbox">
          <Checkbox etykieta="Odświeżaj co 5 s" zaznaczony={automat} onZmiana={setAutomat} />
        </div>
      </div>

      {blad !== null && dane !== null && !bladBezZgodnychDanych && (
        <BladOdswiezania
          czego="logów"
          blad={blad}
          ostatnie={`Widać ostatnio pobrane linie z ${formatujCzas(dane.wygenerowano)}.`}
          onPonow={odswiez}
        />
      )}

      {blad !== null && !ladowanie && bladBezZgodnychDanych ? (
        <StanBledu tytul="Nie udało się wczytać logów" opis={komunikatBleduApi(blad)} akcja={<Button onClick={odswiez}>Spróbuj ponownie</Button>} />
      ) : dane === null ? (
        <StanLadowania tekst="Wczytuję logi..." />
      ) : !dane.istnieje ? (
        <StanPusty tytul="Plik logu jeszcze nie istnieje" opis={`Aplikacja nie zapisała jeszcze nic do pliku ${dane.plik}.`} />
      ) : dane.linie.length === 0 ? (
        <StanPusty
          tytul="Brak linii dla tych filtrów"
          opis="Zmień poziom albo szukany tekst. Filtr działa na ostatnim fragmencie pliku (do 2 MB od końca)."
        />
      ) : (
        <>
          <p className="developer-meta">
            Godziny w czasie lokalnym przeglądarki ({strefa}), jak w reszcie zakładki. Najechanie na godzinę pokazuje zapis z logu
            (UTC), a kopiowanie zachowuje oryginalny zapis.
          </p>
          <div className="blok-logow" role="log" aria-live="off" aria-label="Linie logu aplikacji" tabIndex={0}>
            {linie.map((linia, i) => {
              const p = podzielLinieLogu(linia);
              return (
                <div className="blok-logow__linia" key={klucze[i]}>
                  {p.poziom !== null ? (
                    <>
                      <span className="blok-logow__czas" title={`W logu: ${p.czas}`}>
                        {p.czas === null ? "" : formatujCzas(p.czas)}
                      </span>
                      <span className="blok-logow__poziom">
                        <Badge wariant={wariantPoziomu(p.poziom)}>{p.poziom}</Badge>
                      </span>
                      <span className="blok-logow__tresc">{p.tresc}</span>
                    </>
                  ) : (
                    <span className="blok-logow__tresc blok-logow__tresc--cala">{p.tresc}</span>
                  )}
                </div>
              );
            })}
          </div>
          <p className="developer-meta">
            Plik {dane.plik}: {formatujLiczbe(dane.bajty)} B. W oknie {formatujLiczbe(dane.wOknie)} linii, pasujących{" "}
            {formatujLiczbe(dane.pasujacych)}, pokazano ostatnie {formatujLiczbe(dane.linie.length)}.
            {dane.oknoObciete ? " Starsze linie są poza oknem odczytu (2 MB od końca pliku)." : ""} Stan z{" "}
            {formatujCzas(dane.wygenerowano)}.
          </p>
        </>
      )}
    </Card>
  );
}
